import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Global smooth-scroll wrapper using Lenis.
 * Lenis is enabled ONLY on non-touch (pointer=fine) devices. On phones and
 * tablets we let the browser handle native scrolling, which prevents the
 * touch hijack that was blocking mobile scroll.
 */
const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  const hasCoarsePointer = window.matchMedia?.(
    "(hover: none) and (pointer: coarse)"
  ).matches;
  const hasTouch =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return hasCoarsePointer || hasTouch;
};

export const SmoothScrollProvider = ({ children }) => {
  useEffect(() => {
    if (isTouchDevice()) {
      // Native scrolling on touch devices — no Lenis, no hijack.
      window.__lenis = null;
      return;
    }

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: false,
    });

    window.__lenis = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return children;
};

export const smoothScrollTo = (target) => {
  const el =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!el) return;
  const lenis = window.__lenis;
  if (lenis) {
    // CSS `scroll-margin-top` (see index.css) already provides the header
    // offset — passing offset:0 avoids stacking (Lenis honours scroll-margin).
    lenis.scrollTo(el, { offset: 0, duration: 1.4 });
  } else {
    // Native fallback. Two hops so a competing scroll write in the same
    // frame (e.g. framer-motion measureAllKeyframes) can't cancel us.
    const doScroll = () => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    doScroll();
    // Re-issue on the next animation frame + a short defer.
    requestAnimationFrame(doScroll);
    setTimeout(doScroll, 380);
  }
};
